import { useState, type FormEvent } from 'react';
import styled from 'styled-components';

interface Props {
  /** Inline server rejection (invalid / unusable code); null when clean. */
  readonly error: string | null;
  /** True while the redeem request is in flight — disables the submit. */
  readonly submitting: boolean;
  readonly onSubmit: (code: string) => void;
  /**
   * Host sign-out. Rendered as a "Log Out" link under the form so a user
   * without an access code isn't trapped on this screen (embedded hosts hide
   * the auth shell, leaving no other sign-out control). Omit ⇒ no link.
   */
  readonly onLogout?: () => void;
}

/**
 * Onboarding step of the signed-in cloud flow: the user pastes the one-time
 * access code an operator issued them. The code is an opaque string — no
 * client-side parsing or validation beyond non-empty (trimmed here before
 * submitting, and re-trimmed defensively in the connection hook).
 *
 * Theming: styles read the host's CSS variables (`--text-primary`,
 * `--accent-color`, `--border-color`, `--bg-color-container`, …) — the same
 * contract as the rest of the terminal UI. Rendered by the default
 * `DimeCloudTerminal` phase UI; hosts overriding `renderPhase` can reuse it
 * or supply their own.
 */
export function AccessCodeScreen({
  error,
  submitting,
  onSubmit,
  onLogout,
}: Props): JSX.Element {
  const [code, setCode] = useState('');
  const canSubmit = code.trim().length > 0 && !submitting;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(code.trim());
  };

  return (
    <Wrap>
      <Card onSubmit={submit}>
        <Title>Dime Terminal</Title>
        <Sub>Enter your access code to set up your terminal.</Sub>

        <Label>
          Access code
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="paste your access code"
            spellCheck={false}
            autoCapitalize="off"
            autoComplete="off"
            $error={error != null}
          />
        </Label>
        {error != null && <Err>{error}</Err>}

        <Button type="submit" disabled={!canSubmit}>
          {submitting ? 'Checking…' : 'Continue'}
        </Button>

        {onLogout != null && (
          <LogOutLink type="button" onClick={onLogout}>
            Log Out
          </LogOutLink>
        )}
      </Card>
    </Wrap>
  );
}

const Wrap = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.form`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--bg-color-container);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary);
`;

const Sub = styled.p`
  margin: 0 0 8px;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--text-label);
`;

const Input = styled.input<{ $error?: boolean }>`
  padding: 9px 11px;
  border-radius: 7px;
  border: 1px solid
    ${({ $error }) => ($error ? 'var(--error-color)' : 'var(--border-color)')};
  background: rgba(var(--color-lightest), 0.03);
  color: var(--text-primary);
  font-size: 0.85rem;
  outline: none;

  &:focus {
    border-color: var(--accent-color);
  }
`;

const Err = styled.div`
  font-size: 0.7rem;
  color: var(--error-color);
  margin-top: -4px;
`;

const Button = styled.button`
  margin-top: 8px;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: var(--accent-color);
  color: var(--cta-text-color);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LogOutLink = styled.button`
  align-self: center;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-secondary);
  font-size: 0.75rem;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;
